// CSS-as-string for iframe injection — see StoryContentsFrame/story.tsx for context.
// eslint-disable-next-line import/no-unresolved
import storyContentStyles from '@opengov/stories-shared/styles/story-modules.css?inline';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { IStoryWithContent } from '@opengov/stories-shared/types';
import {
  applyCustomCssToScope,
  generateHTML,
  getFullPageContent,
  getStoryCustomCssScopeId,
  replaceStoryContentsWithMergeTagValues,
  sortStoryContents
} from '@opengov/stories-shared/utils';
import cx from 'classnames2';
import React, { useEffect, useState } from 'react';
import Frame from 'react-frame-component';

import { IPublicationCustomCssMessages } from '../../../../../types';
import {
  HIGHLIGHT_ACTIVE_ELEMENT_CLASSNAME,
  HIGHLIGHT_ELEMENT_CLASSNAME,
  PUBLICATION_CUSTOM_CSS_ID_ATTR,
  STORY_CUSTOM_CSS_ID_ATTR
} from '../../../../utils/stylesheetsUtils';
import { FolderSvg } from '../../../StoryList/StoryListContent/FolderSvg';

import styles from './StoryPreview.scss';
import { useProgressTrackerSettingsContext } from '../../../StoriesSettings/ProgressTrackerSettings/context/useProgressTrackerSettingsContext';
import { useStoryProposals } from '../hooks/useStoryProposals';

export interface IStoryPreviewProps {
  activeElement: HTMLElement | null;
  entityId: number;
  iframeDocumentRef: React.MutableRefObject<Document | null>;
  messages: IPublicationCustomCssMessages;
  publicationCss: string;
  selectedStoryId: string;
  story: IStoryWithContent | null;
  storyCss: string;
  setActiveElement(activeElement: HTMLElement | null): void;
}

export const StoryPreview = (props: IStoryPreviewProps) => {
  const {
    activeElement,
    entityId,
    iframeDocumentRef,
    messages,
    publicationCss,
    selectedStoryId,
    story,
    storyCss,
    setActiveElement
  } = props;
  const [iframeContent, setIframeContent] = useState<string | null>(null);
  const [hoveredElement, setHoveredElement] = useState<HTMLDivElement | null>(null);
  const { progressTrackerSettings } = useProgressTrackerSettingsContext();

  const proposalsQuery = useStoryProposals(
    selectedStoryId && entityId > 0 ? { storyId: selectedStoryId, entityId } : null,
    messages.somethingWentWrongError
  );
  const mergeTags = proposalsQuery.data?.mergeTags;
  const handleElementClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveElement(event.target);
  };
  const handleElementMouseOver = (event) => setHoveredElement(event.target);
  const handleElementMouseLeave = () => setHoveredElement(null);

  const subscribeIframeEvents = (ref) => {
    const iframeDocument = ref?.node?.contentWindow?.document;
    if (iframeDocument && !iframeDocumentRef.current) {
      iframeDocumentRef.current = iframeDocument;
      iframeDocument.body.addEventListener('click', handleElementClick);
      iframeDocument.body.addEventListener('mouseover', handleElementMouseOver);
      iframeDocument.body.addEventListener('mouseleave', handleElementMouseLeave);
    }
  };

  useEffect(() => {
    setIframeContent(null);
    iframeDocumentRef.current = null;
  }, [selectedStoryId]);

  useEffect(() => {
    if (!story || !selectedStoryId || !mergeTags) return;
    const updatedContents = replaceStoryContentsWithMergeTagValues(sortStoryContents(story.contents), mergeTags);
    const isStoryWithProgressTracker = updatedContents.some(
      (content) => Array.isArray(content.contentChartConfig) && content.contentChartConfig.length > 0
    );
    const { content, containers } = getFullPageContent(
      updatedContents,
      isStoryWithProgressTracker,
      story.contents[0].entityId,
      progressTrackerSettings
    );
    setIframeContent(
      generateHTML({
        id: story._id,
        title: story.title,
        description: story.description || '',
        fonts: [],
        content,
        hasGallery: true,
        isForCustomCssInspector: true,
        options: {},
        isFollowable: story.isFollowable,
        containers,
        addIgnorePortalScript: true,
        isStoryWithProgressTracker
      })
    );
  }, [story, selectedStoryId, mergeTags, progressTrackerSettings]);

  useEffect(() => {
    if (hoveredElement) hoveredElement.classList.add(HIGHLIGHT_ELEMENT_CLASSNAME);
    return () => {
      if (hoveredElement) hoveredElement.classList.remove(HIGHLIGHT_ELEMENT_CLASSNAME);
    };
  }, [hoveredElement]);

  useEffect(() => {
    if (activeElement) activeElement.classList.add(HIGHLIGHT_ACTIVE_ELEMENT_CLASSNAME);
    return () => {
      if (activeElement) activeElement.classList.remove(HIGHLIGHT_ACTIVE_ELEMENT_CLASSNAME);
    };
  }, [activeElement]);

  if (!selectedStoryId) {
    return (
      <div data-test="storyEmptyPreview" className={cx(styles.container, styles.emptyPreview)}>
        <FolderSvg />
        <Typography variant="body2" color="text.secondary">{messages.selectStoryToCustomize}</Typography>
      </div>
    );
  }

  if (!iframeContent) {
    return (
      <div data-test="storyPreviewLoadingSpinner" role="status" aria-label="Loading" className={cx(styles.container, styles.emptyPreview)}>
        <CircularProgress />
      </div>
    );
  }

  const scopeId = getStoryCustomCssScopeId();
  const scopeSelector = `#${scopeId}`;
  return (
    <div data-test="storyPreview" className={styles.container}>
      <Frame
        ref={(ref) => {
          setTimeout(() => subscribeIframeEvents(ref), 50);
        }}
        key={selectedStoryId}
        head={
          <>
            <style>{storyContentStyles}</style>
            <style data-id={PUBLICATION_CUSTOM_CSS_ID_ATTR}>
              {applyCustomCssToScope(publicationCss, scopeSelector)}
            </style>
            <style data-id={STORY_CUSTOM_CSS_ID_ATTR}>{applyCustomCssToScope(storyCss, scopeSelector)}</style>
          </>
        }
        initialContent={iframeContent}
      />
    </div>
  );
};
